/**
 * YearDerivationService - Dynamic year derivation and management
 * 
 * This service provides dynamic year derivation to replace all hardcoded years
 * in the application. It ensures that years are calculated based on current
 * date and data availability rather than being hardcoded.
 */

import { log } from './LoggingService';

export interface YearRange {
  startYear: number;
  endYear: number;
  years: number[];
}

export interface YearContext {
  currentYear: number;
  previousYear: number;
  nextYear: number;
  dataYears: number[];
  availableYears: number[];
  defaultYear: number;
}

export interface YearVisibility {
  show2023: boolean;
  show2024: boolean;
  show2025: boolean;
  show2026: boolean;
}

export class YearDerivationService {
  private static instance: YearDerivationService;
  private currentYear: number;
  private dataYears: number[] = [];
  private yearCache: Map<string, YearContext> = new Map();

  private constructor() {
    this.currentYear = new Date().getFullYear();
    this.initializeDataYears();
  }

  public static getInstance(): YearDerivationService {
    if (!YearDerivationService.instance) {
      YearDerivationService.instance = new YearDerivationService();
    }
    return YearDerivationService.instance;
  }

  /**
   * Initialize available data years based on current year and data availability
   */
  private initializeDataYears(): void {
    const currentYear = this.currentYear;
    
    // Typically we have data for current year and previous 2-3 years
    this.dataYears = [
      currentYear - 2, // 2 years ago
      currentYear - 1, // Last year
      currentYear,     // Current year
      currentYear + 1  // Next year (for projections)
    ].filter(year => year >= 2020); // Only include years from 2020 onwards

    log.debug(`Initialized data years: [${this.dataYears.join(', ')}]`, 'YearDerivationService');
  }

  /**
   * Get the current year context
   */
  getYearContext(): YearContext {
    const cacheKey = 'current';
    const cached = this.yearCache.get(cacheKey);
    
    if (cached) {
      return cached;
    }

    const context: YearContext = {
      currentYear: this.currentYear,
      previousYear: this.currentYear - 1,
      nextYear: this.currentYear + 1,
      dataYears: [...this.dataYears],
      availableYears: this.getAvailableYears(),
      defaultYear: this.getDefaultYear()
    };

    this.yearCache.set(cacheKey, context);
    return context;
  }

  /**
   * Get available years for data display
   */
  getAvailableYears(): number[] {
    return this.dataYears.filter(year => {
      // Filter based on data availability and business rules
      const currentYear = this.currentYear;
      
      // Always include current year and previous year
      if (year === currentYear || year === currentYear - 1) {
        return true;
      }
      
      // Include next year only if we're in Q4 or later
      if (year === currentYear + 1) {
        const currentMonth = new Date().getMonth();
        return currentMonth >= 9; // October or later
      }
      
      // Include historical years
      return year < currentYear;
    });
  }

  /**
   * Get the default year for new data
   */
  getDefaultYear(): number {
    const currentYear = this.currentYear;
    const currentMonth = new Date().getMonth();
    
    // If we're in Q4, default to next year for projections
    if (currentMonth >= 9) {
      return currentYear + 1;
    }
    
    // Otherwise default to current year
    return currentYear;
  }

  /**
   * Get year range for a specific timeframe
   */
  getYearRange(timeframe: string): YearRange {
    const currentYear = this.currentYear;
    const availableYears = this.getAvailableYears();
    
    let startYear: number;
    let endYear: number;

    switch (timeframe.toLowerCase()) {
      case '1w':
      case '2w':
      case '4w':
      case '12w':
        // Short timeframes - current year only
        startYear = currentYear;
        endYear = currentYear;
        break;
      
      case '6m':
        // 6 months - current year
        startYear = currentYear;
        endYear = currentYear;
        break;
      
      case 'ytd':
        // Year to date - current year
        startYear = currentYear;
        endYear = currentYear;
        break;
      
      case 'ttm':
        // Trailing 12 months - current and previous year
        startYear = currentYear - 1;
        endYear = currentYear;
        break;
      
      case '1y':
        // 1 year - previous year
        startYear = currentYear - 1;
        endYear = currentYear - 1;
        break;
      
      case '2y':
        // 2 years - previous 2 years
        startYear = currentYear - 2;
        endYear = currentYear - 1;
        break;
      
      default:
        // Default to current year
        startYear = currentYear;
        endYear = currentYear;
    }

    // Ensure years are within available data range
    startYear = Math.max(startYear, Math.min(...availableYears));
    endYear = Math.min(endYear, Math.max(...availableYears));

    const years = [];
    for (let year = startYear; year <= endYear; year++) {
      if (availableYears.includes(year)) {
        years.push(year);
      }
    }

    return { startYear, endYear, years };
  }

  /**
   * Get year visibility state for UI components
   */
  getYearVisibility(): YearVisibility {
    const availableYears = this.getAvailableYears();
    
    return {
      show2023: availableYears.includes(2023),
      show2024: availableYears.includes(2024),
      show2025: availableYears.includes(2025),
      show2026: availableYears.includes(2026)
    };
  }

  /**
   * Get series names for charts based on available years
   */
  getSeriesNames(): string[] {
    const availableYears = this.getAvailableYears();
    return availableYears.map(year => year.toString());
  }

  /**
   * Get year for a specific data point based on date
   */
  getYearForDate(date: Date | string): number {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.getFullYear();
  }

  /**
   * Get year for a specific week
   */
  getYearForWeek(year: number, week: number): number {
    // ISO week calculation - week 1 is the first week with at least 4 days in the new year
    const jan4 = new Date(year, 0, 4);
    const week1Start = new Date(jan4);
    week1Start.setDate(jan4.getDate() - (jan4.getDay() + 6) % 7);
    
    const weekStart = new Date(week1Start);
    weekStart.setDate(week1Start.getDate() + (week - 1) * 7);
    
    return weekStart.getFullYear();
  }

  /**
   * Check if a year is available for data
   */
  isYearAvailable(year: number): boolean {
    return this.getAvailableYears().includes(year);
  }

  /**
   * Get the most recent year with data
   */
  getMostRecentYear(): number {
    const availableYears = this.getAvailableYears();
    return Math.max(...availableYears);
  }

  /**
   * Get the oldest year with data
   */
  getOldestYear(): number {
    const availableYears = this.getAvailableYears();
    return Math.min(...availableYears);
  }

  /**
   * Update data years (called when new data is available)
   */
  updateDataYears(newYears: number[]): void {
    this.dataYears = [...new Set([...this.dataYears, ...newYears])].sort();
    this.yearCache.clear(); // Clear cache when data years change
    
    log.debug(`Updated data years: [${this.dataYears.join(', ')}]`, 'YearDerivationService');
  }

  /**
   * Get year context for a specific timeframe
   */
  getYearContextForTimeframe(timeframe: string): YearContext {
    const cacheKey = `timeframe_${timeframe}`;
    const cached = this.yearCache.get(cacheKey);
    
    if (cached) {
      return cached;
    }

    const yearRange = this.getYearRange(timeframe);
    const context: YearContext = {
      currentYear: this.currentYear,
      previousYear: this.currentYear - 1,
      nextYear: this.currentYear + 1,
      dataYears: yearRange.years,
      availableYears: yearRange.years,
      defaultYear: yearRange.startYear
    };

    this.yearCache.set(cacheKey, context);
    return context;
  }

  /**
   * Get year labels for display
   */
  getYearLabels(): Record<number, string> {
    const availableYears = this.getAvailableYears();
    const labels: Record<number, string> = {};
    
    availableYears.forEach(year => {
      const currentYear = this.currentYear;
      if (year === currentYear) {
        labels[year] = `${year} (Current)`;
      } else if (year === currentYear - 1) {
        labels[year] = `${year} (Previous)`;
      } else if (year === currentYear + 1) {
        labels[year] = `${year} (Projected)`;
      } else {
        labels[year] = year.toString();
      }
    });

    return labels;
  }

  /**
   * Clear year cache (useful for testing or when data changes)
   */
  clearCache(): void {
    this.yearCache.clear();
    log.debug('Year cache cleared', 'YearDerivationService');
  }

  /**
   * Get debug information about year derivation
   */
  getDebugInfo(): {
    currentYear: number;
    dataYears: number[];
    availableYears: number[];
    yearVisibility: YearVisibility;
    seriesNames: string[];
    yearLabels: Record<number, string>;
  } {
    return {
      currentYear: this.currentYear,
      dataYears: this.dataYears,
      availableYears: this.getAvailableYears(),
      yearVisibility: this.getYearVisibility(),
      seriesNames: this.getSeriesNames(),
      yearLabels: this.getYearLabels()
    };
  }
}

// Export singleton instance
export const yearDerivationService = YearDerivationService.getInstance();
